import { Box, Image } from '@chakra-ui/react';
import { IconCheck } from '@tabler/icons-react';
import React, { useEffect, useState } from 'react';
import 'moment/locale/it';
import * as Yup from 'yup';
import { Formik } from 'formik';
import DropzoneFormikInput from '../../utilities/Dropzone';
import { v4 as uuidv4 } from 'uuid';
import { useOffices } from '../../../hooks/Offices/useOffices';
import { toast } from 'react-toastify';
import { useOfficesCities } from '../../../hooks/OfficesCities/useOfficesCities';
import { UIModal, UIModalBody, UIModalButtons, UiModalInternal } from '../../utilities/Modal';
import UITypography from '../../utilities/Typography';
import { UIFormControlBox } from '../../utilities/Form';
import { UICheckbox, UIInput } from '../../utilities/Input';
import { UIButton } from '../../utilities/Button';
import UIInputAutocomplete from '../../utilities/InputSelect';

const ModalOfficeAdmin = ({ modalOpen, handleModalClose, office }) => {
  const [uuid, setUuid] = useState(uuidv4());
  const [cities, setCities] = useState([]);
  const [officeData, setOfficeData] = useState(null);

  const { onUpdateOfficeAdmin, onCreateOfficeAdmin, onGetOfficeAdmin } = useOffices();
  const { onListOfficesCitiesAdmin } = useOfficesCities();

  useEffect(() => {
    setUuid(uuidv4());
    onListOfficesCitiesAdmin().then((res) => {
      setCities(res);
    });

    if (!modalOpen || !office) return;
    onGetOfficeAdmin({
      office_id: office?.id
    }).then((res) => {
      setOfficeData(res);
    });
  }, [office, modalOpen]);

  const handleSubmit = async (values) => {
    const { name, image, address, phone, email, city_id, active } = values;

    if (office?.id) {
      onUpdateOfficeAdmin({
        office_id: office.id,
        office_data: {
          name,
          image,
          address,
          phone,
          email,
          city_id,
          active
        }
      }).then((res) => {
        if (res?.response) {
          toast(res?.response, {
            style: {
              fontSize: '14px',
              backgroundColor: '#00e676',
              color: '#ffffff'
            }
          });
          handleModalClose();
        }
      });
    } else {
      onCreateOfficeAdmin({
        office_data: {
          uuid,
          name,
          image,
          address,
          phone,
          email,
          city_id,
          active
        }
      }).then((res) => {
        if (res?.response) {
          toast(res?.response, {
            style: {
              fontSize: '14px',
              backgroundColor: '#00e676',
              color: '#ffffff'
            }
          });
          handleModalClose();
        }
      });
    }
  };

  const handleInternalClose = () => {
    setOfficeData(null);
    handleModalClose();
  };

  return (
    <UIModal open={modalOpen} onClose={handleInternalClose} allowFullScreen={true} className={'modal'}>
      <UiModalInternal
        onClose={handleInternalClose}
        isLoading={office && officeData === null}
        containerSx={{
          width: '80%',
          maxWidth: '800px'
        }}
      >
        <Formik
          initialValues={{
            name: officeData?.name ?? '',
            image: officeData?.image ?? '',
            address: officeData?.address ?? '',
            phone: officeData?.phone ?? '',
            email: officeData?.email ?? '',
            city_id: officeData?.city_id ?? '',
            active: officeData?.active ?? false
          }}
          validationSchema={Yup.object().shape({
            name: Yup.string().max(255).required('Nome richiesto'),
            image: Yup.string().max(255).required('Immagine richiesta'),
            address: Yup.string().max(255).required('Indirizzo richiesto'),
            phone: Yup.string().max(255).nullable(),
            email: Yup.string().max(255).nullable(),
            city_id: Yup.number().required('Città richiesta'),
            active: Yup.boolean().nullable()
          })}
          onSubmit={(values, { setErrors }) => handleSubmit(values, setErrors)}
          enableReinitialize={true}
        >
          {({ errors, submitForm, touched, values, setFieldValue }) => (
            <form onSubmit={handleSubmit} id={'appointment-form'}>
              <UIModalBody
                sx={{
                  bgcolor: 'transparent'
                }}
              >
                <UITypography
                  title={'Dati ufficio'}
                  size={'20px'}
                  sxCustom={{
                    textAlign: 'left',
                    color: '#101828',
                    mt: '30px',
                    py: '2px',
                    px: '20px'
                  }}
                />
                <UIFormControlBox error={Boolean(touched.name && errors.name)} label={'Nome'}>
                  <UIInput value={values.name} onChange={(e) => setFieldValue('name', e.target.value)} />
                </UIFormControlBox>

                <UIFormControlBox label={'Immagine'} error={Boolean(touched.image && errors.image)} error_message={errors?.image}>
                  <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'} p={'0 10px'}>
                    <Image src={values.image} w={'100px'} h={'100px'} objectFit={'cover'} />
                    <DropzoneFormikInput
                      variant={'office_image'}
                      setFieldValue={setFieldValue}
                      field={'image'}
                      folder={'offices'}
                      label={'Carica'}
                      extra_data={{
                        office_uuid: office?.uuid ?? uuid
                      }}
                    />
                  </Box>
                </UIFormControlBox>

                <UIFormControlBox label={'Città'} error_message={errors?.city_id} error={Boolean(touched.city_id && errors.city_id)}>
                  <UIInputAutocomplete
                    value={Number(values?.city_id)}
                    setValue={(value) => {
                      if (!value) return;
                      setFieldValue('city_id', value?.id);
                    }}
                    options={cities?.map((city) => {
                      return {
                        id: Number(city?.id),
                        label: `${city?.name}`
                      };
                    })}
                    needTimeout={false}
                  />
                </UIFormControlBox>

                <UIFormControlBox error={Boolean(touched.address && errors.address)} label={'Indirizzo'}>
                  <UIInput value={values.address} onChange={(e) => setFieldValue('address', e.target.value)} />
                </UIFormControlBox>

                <UIFormControlBox error={Boolean(touched.phone && errors.phone)} label={'Telefono'}>
                  <UIInput value={values.phone} onChange={(e) => setFieldValue('phone', e.target.value)} />
                </UIFormControlBox>

                <UIFormControlBox error={Boolean(touched.email && errors.email)} label={'Email'}>
                  <UIInput value={values.email} onChange={(e) => setFieldValue('email', e.target.value)} />
                </UIFormControlBox>
                <UIFormControlBox sx={{}}>
                  <UICheckbox
                    name={'active'}
                    label={'Attivo'}
                    checked={values?.active}
                    onChange={(event) => setFieldValue('active', event.target.checked)}
                  />
                </UIFormControlBox>
              </UIModalBody>
              <UIModalButtons>
                <UIButton type={'submit'} onClick={() => submitForm()} icon={<IconCheck stroke={'1.64'} size={'26px'} />} />
              </UIModalButtons>
            </form>
          )}
        </Formik>
      </UiModalInternal>
    </UIModal>
  );
};
export default ModalOfficeAdmin;
